/**
 * POI (Point of Interest) calculation utilities for route mapping
 * Extracts POI calculation logic from useMapsApiRouteData hook
 */

import { PointOfInterest } from '../../components/SectionBlocks/blocks/Charts/RouteCharts';
import { RichGeopoint } from '../../../../types/routeBlockType';
import { RouteData } from '../../../../types/mapsApiTypes';

/**
 * Calculates POIs using backend legs data (most accurate method)
 * Each leg represents the distance between consecutive waypoints
 *
 * @param routepoints Array of waypoints
 * @param elevation Elevation profile array
 * @param routeData Route data from backend (must include legs)
 * @returns Array of points of interest with distances and altitudes
 */
export const calculatePOIsFromLegs = (
  routepoints: RichGeopoint[],
  elevation: number[],
  routeData: RouteData,
): PointOfInterest[] => {
  if (!routeData.legs || routeData.legs.length === 0) {
    throw new Error('Route data must include legs for accurate POI calculation');
  }

  const waypoints = [...routepoints];
  const start = waypoints.shift();
  const end = waypoints.pop();

  if (!start || !end) {
    throw new Error('Invalid route points: must have at least start and end');
  }

  const routeLength = elevation.length;
  const calculatedPOIs: PointOfInterest[] = [{ ...start, kmFromStart: 0, altitude: elevation[0] }];

  let accumulatedDistanceKm = 0;

  // Add waypoint POIs using cumulative leg distances
  waypoints.forEach((waypoint, index) => {
    // Add the distance of the leg leading to this waypoint
    accumulatedDistanceKm += routeData.legs![index].distanceMeters / 1000;

    // Round to nearest km and ensure within elevation array bounds
    const kmFromStart = Math.min(Math.round(accumulatedDistanceKm), routeLength - 1);

    calculatedPOIs.push({
      ...waypoint,
      kmFromStart,
      altitude: elevation[kmFromStart] || elevation[0],
    });
  });

  // Add end POI - use total route distance
  const lastLegIndex = routeData.legs.length - 1;
  accumulatedDistanceKm += routeData.legs[lastLegIndex].distanceMeters / 1000;
  const endKmFromStart = Math.min(Math.round(accumulatedDistanceKm), routeLength - 1);

  calculatedPOIs.push({
    ...end,
    kmFromStart: endKmFromStart,
    altitude: elevation[endKmFromStart],
  });

  return calculatedPOIs;
};

/**
 * Calculates POIs using total distance estimation (fallback method)
 * Less accurate than using legs data - distributes waypoints evenly
 *
 * @param routepoints Array of waypoints
 * @param elevation Elevation profile array
 * @param routeData Route data from backend (uses total distance)
 * @returns Array of points of interest with estimated distances and altitudes
 */
export const calculatePOIsFromDistance = (
  routepoints: RichGeopoint[],
  elevation: number[],
  routeData: RouteData,
): PointOfInterest[] => {
  const waypoints = [...routepoints];
  const start = waypoints.shift();
  const end = waypoints.pop();

  if (!start || !end) {
    throw new Error('Invalid route points: must have at least start and end');
  }

  const routeLength = elevation.length;
  const totalDistanceMeters = routeData.distance.meters;
  const totalDistanceKm = totalDistanceMeters / 1000;

  const calculatedPOIs: PointOfInterest[] = [{ ...start, kmFromStart: 0, altitude: elevation[0] }];

  // Estimate waypoint positions by evenly distributing them along the route
  // This is a rough approximation and should only be used as a fallback
  const totalPoints = waypoints.length + 2; // start + waypoints + end
  waypoints.forEach((waypoint, index) => {
    const estimatedKm = Math.round((totalDistanceKm / (totalPoints - 1)) * (index + 1));
    const kmFromStart = Math.min(estimatedKm, routeLength - 1);

    calculatedPOIs.push({
      ...waypoint,
      kmFromStart,
      altitude: elevation[kmFromStart] || elevation[0],
    });
  });

  calculatedPOIs.push({
    ...end,
    kmFromStart: Math.min(Math.round(totalDistanceKm), routeLength - 1),
    altitude: elevation[Math.min(Math.round(totalDistanceKm), routeLength - 1)],
  });

  return calculatedPOIs;
};

/**
 * Creates basic POIs without elevation data
 * Uses sequential numbering for distances
 *
 * @param routepoints Array of waypoints
 * @returns Array of basic points of interest (altitude = 0, distance = index)
 */
export const createBasicPOIs = (routepoints: RichGeopoint[]): PointOfInterest[] => {
  const waypoints = [...routepoints];
  const start = waypoints.shift();
  const end = waypoints.pop();

  if (!start || !end) {
    throw new Error('Invalid route points: must have at least start and end');
  }

  return [
    { ...start, kmFromStart: 0, altitude: 0 },
    ...waypoints.map((wp, idx) => ({
      ...wp,
      kmFromStart: idx + 1,
      altitude: 0,
    })),
    { ...end, kmFromStart: waypoints.length + 1, altitude: 0 },
  ];
};
